import Vue from "vue";
import Config from "@/config";
import {sortByAuth} from "@/utils/group";
import {date2Iso} from "@/utils/date";
import uiMutations from "@/store/ui/mutations";
import router from "@/router";
import {flatArray} from "@/utils/utils";
import {fullContextItem, jointContexts, obtainPickSingleParent} from "@/utils/pick";
import {initPicksInArea, initFavoriteUsersInArea} from "@/utils/area";
import {
    appendContextFromPayload,
    appendPayloadWithContextIds,
    applyContextUpdateFromPayload,
    applyParentsTo, applyTaskUpdateFromPayload, routeToPersonalTasks,
    sortByPrio,
    topicTaskSyncedProps
} from "@/utils/task";
import {parseContextIds} from "@/utils/context";
import store from "@/store";
import storage from "@/utils/storage";

/**
 * Синхронизирует копии тега в быстрых тегах групп с подтверждённым сервером состоянием:
 * обновляет название/цвет тега или убирает тег, если он удалён.
 * @param state
 * @param tag тег ({id, name, color})
 * @param isDeleted тег удалён
 */
const syncQuickTags = (state, tag, isDeleted = false) => {
    state.session.area?.groups?.forEach(group => {
        if (!group.quickTags?.some(el => el.contextId === tag.id)) return;
        Vue.set(group, "quickTags", isDeleted
            ? group.quickTags.filter(el => el.contextId !== tag.id)
            : group.quickTags.map(el => el.contextId === tag.id ? {...el, name: tag.name, color: tag.color} : el));
    });
};

/**
 * Синхронизирует закреплённые теги пользователя с подтверждённым сервером состоянием:
 * обновляет название/цвет тега или убирает тег, если он удалён.
 * @param state
 * @param tag тег ({id, name, color})
 * @param isDeleted тег удалён
 */
const syncPinnedTags = (state, tag, isDeleted = false) => {
    const area = state.session.area;
    if (!area?.pinnedTags?.some(el => el.id === tag.id)) return;
    Vue.set(area, "pinnedTags", isDeleted
        ? area.pinnedTags.filter(el => el.id !== tag.id)
        : area.pinnedTags.map(el => el.id === tag.id ? {...el, name: tag.name, color: tag.color} : el));
};

const mutations = {
    /**
     * Applies root context item + root context tasks.
     * @param state
     * @param payload
     */
    contextItem: (state, payload) => {
        console.log("MUTATION context/item", payload);
        if (payload?.isFromServer) {
            // 1. set parents to tasks - for convenience
            // 1.1. set .parent to context tasks
            applyParentsTo(payload.tasks?.items, state.page.context.item);
            // 1.2. set .parent to context item itself
            //console.log("state.page.context.parents?.length: "+state.page.context.parents?.length);
            if (state.page.context.parents?.length) {
                let children = [state.page.context.item];
                for (let i = state.page.context.parents.length - 1; i >= 0; i--) {
                    //console.log("SETTING PARENT TO "+children[0].id+": "+state.page.context.parents[i].id);
                    applyParentsTo(children, state.page.context.parents[i]);
                    children = [state.page.context.parents[i]];
                }
            }
            /*state.page.context.parents?.forEach(x => {
                applyParentsTo(payload.tasks?.items, state.page.context.item);
            });*/

            // 2. remember last context id
            storage.setSession(Config.SESSION_LAST_CONTEXT_ID, payload?.context?.item?.contextId);

            // 3. refresh plain array of tasks
            const tree = state.page.tasks.items;
            state.page.tasks.flatItems = flatArray(tree);

            // 4. задача в панели могла загрузиться раньше списка (дерево пользователя грузится дольше плоского списка) -
            // ответ списка сбрасывает выбор, поэтому выбираем открытую задачу заново
            const openedTask = state.page.task.item;
            const route = router.currentRoute;
            if (openedTask?.id && route?.name === "context-task"
                && (route.params.taskId === openedTask.id || route.params.taskId === openedTask.auth)
                && !state.page.context.selectedTasks?.some(el => el.id === openedTask.id)) {
                store.commit("contextAppendSelectedTask", {item: openedTask, withFullReplace: true});
            }

            //mutations.contextClearSelectedTasks(state);
        } else {
            // add context filters from local storage
            appendPayloadWithContextIds(state, payload);
            // пользовательский контекст запрашиваем деревом (на других контекстах флаг игнорируется бэкендом)
            if (state.ui.isUserContextTree) payload.userTree = true;
        }
    },
    /**
     * Контексты цепочки (панельный режим): заполняет page.contexts.
     * До запроса: в payload.appendContextIds кладём id контекстов из payload.contextIdChain, которых ещё нет в page.contexts.
     * После ответа: данные из contextsAppend (или contextsUpdate - перезагрузка уже имеющихся контекстов по
     * payload.updateContextIds) ставим в page.contexts на позиции по payload.contextIdChain,
     * остальные контексты page.contexts не трогаем.
     * @param state
     * @param payload
     */
    contextChain: (state, payload) => {
        console.log("MUTATION context/chain", payload);
        const chainIds = payload?.contextIdChain?.split(Config.CONTEXT_ID_CHAIN_SEP).filter(el => !!el) || [];
        const isOfId = (item, id) => item?.id === id || item?.auth === id;

        if (payload?.isFromServer) {
            // contextsAppend - новые контексты цепочки, contextsUpdate - перезагруженные (например, после смены фильтра)
            const entries = payload.contextsAppend || payload.contextsUpdate;
            if (!entries) return;

            const contexts = state.page.contexts ? [...state.page.contexts] : [];
            entries.forEach(entry => {
                const index = chainIds.findIndex(id => isOfId(entry.item, id));
                // контекст, которого нет в цепочке, пропускаем
                if (index < 0) return;
                contexts[index] = entry;
            });
            state.page.contexts = contexts;
        } else if (!payload.updateContextIds) {
            payload.appendContextIds = chainIds.filter(id => !state.page.contexts?.some(el => isOfId(el?.item, id)));
        }
    },
    /**
     * Appends tasks to a task context.
     * Do not use with root context (group, user). Use context/item instead.
     * @param state
     * @param payload
     */
    contextAppendChildTasks: (state, payload) => {
        console.log("MUTATION context/contextAppendChildTasks", payload);

        if (payload?.isFromServer) {
        } else {
            const context = payload.context;
            //const parent = state.page.tasks.flatItems?.find(el=>el.id===context.id);
            //console.log("state.page.tasks.flatItems", state.page.tasks.flatItems.map(el=>el.id));
            if (!context) return;

            //console.log("PARENT FOUND", context);

            // there is a valid array - it may be even empty or null - but it should not be undefined!
            Vue.set(context, "children", payload.items);
            store.commit("taskAppendToFlatItems", payload.items);

            // set parents to tasks - for convenience
            applyParentsTo(payload.items, context);
        }
    },
    contextCreate: (state, payload) => {
        console.log("MUTATION context/create", payload);

        // after creation - call this
        payload.mutation = "contextAppend";

        // after creation - broadcast to subscribers of this
        /*const broadcast = [{
            entity: "context",
            auth: payload.contextId,
        }];
        payload.broadcast = broadcast;
        console.log("broadcast", broadcast);*/
    },
    contextCreateGroup: (state, payload) => {
        console.log("MUTATION context/createGroup", payload);

        if (payload?.isFromServer) {
            appendContextFromPayload(state, payload);
        }
    },
    contextCreateTag: (state, payload) => {
        console.log("MUTATION context/createTag", payload);

        if (payload?.isFromServer) {
            appendContextFromPayload(state, payload);
        }
    },
    contextShare: (state, payload) => {
        if (payload?.isFromServer) {
            const item = payload.contextUpdate?.item;
            if (!item) return;
            item.isOwner = '' + item.creatorId === '' + state.session.user.id;
            if (!item.isOwner) delete item.sharedUserIds;
            const existing = state.session.area.tags?.find(el => el.id === item.id);
            if (existing) applyContextUpdateFromPayload(state, payload);
            else appendContextFromPayload(state, {contextAppend: {item}});
        } else {
            const targetUser = state.session.area.users?.find(el => '' + el.id === '' + payload.userId);
            if (targetUser?.id) payload.broadcast = {entity: "user", auth: targetUser.id};
        }
    },
    contextUnshare: (state, payload) => {
        if (payload?.isFromServer) {
            const item = payload.contextUpdate?.item;
            if (!item) return;
            if ('' + item.creatorId === '' + state.session.user.id) {
                item.isOwner = true;
                applyContextUpdateFromPayload(state, payload);
            } else {
                const index = state.session.area.tags?.findIndex(el => el.id === item.id);
                if (index >= 0) state.session.area.tags.splice(index, 1);
            }
        } else {
            const targetUser = state.session.area.users?.find(el => '' + el.id === '' + payload.userId);
            if (targetUser?.id) payload.broadcast = {entity: "user", auth: targetUser.id};
        }
    },
    contextPick: (state, payload) => {
        console.log("MUTATION context/pick", payload);

        // after creation - call this
        payload.mutation = "contextAppend";

        // after creation - broadcast to subscribers of this
        const broadcast = [{
            entity: "context",
            auth: payload.contextId,
        }];
        payload.broadcast = broadcast;
        console.log("broadcast", broadcast);
    },
    contextAppend: (state, payload) => {
        console.log("MUTATION context/append", payload);

        if (payload?.isFromServer) {
            appendContextFromPayload(state, payload);
        }
    },
    contextUpdate: (state, payload) => {
        console.log("MUTATION context/update", payload);

        if (payload?.isFromServer) {
            applyContextUpdateFromPayload(state, payload);
            const tag = payload.contextUpdate?.item;
            if (tag?.isTag) {
                syncQuickTags(state, tag);
                syncPinnedTags(state, tag);
            }
        } else {

        }
    },
    contextFollowTask: (state, payload) => {
        console.log("MUTATION context/followTask", payload);

        if (payload?.isFromServer) {
            applyTaskUpdateFromPayload(state, payload);
        } else {

        }
    },
    contextUnfollowTask: (state, payload) => {
        console.log("MUTATION context/unfollowTask", payload);

        if (payload?.isFromServer) {
            applyTaskUpdateFromPayload(state, payload);
        } else {

        }
    },
    /** Закреплённые теги пользователя: обновление только из ответа сервера */
    contextSetPinnedTags: (state, payload) => {
        if (payload?.isFromServer && payload.pinnedTags?.items) {
            Vue.set(state.session.area, "pinnedTags", payload.pinnedTags.items);
        }
    },
    /** Быстрые теги группы: обновление только из ответа сервера (без оптимистичных изменений) */
    contextSetQuickTags: (state, payload) => {
        if (payload?.isFromServer) {
            applyContextUpdateFromPayload(state, payload);
        }
    },
    contextUnpinQuickTag: (state, payload) => {
        if (payload?.isFromServer) {
            applyContextUpdateFromPayload(state, payload);
        }
    },
    /**
     * Срочная задача пользователя: обновление только из ответа сервера (taskUpdate с urgentInGroupIds).
     * Сервер может сменить приоритет задачи на Important и пересортировать контексты - индексы приходят в taskUpdate.
     */
    contextSetUrgentTask: (state, payload) => {
        if (payload?.isFromServer) {
            applyTaskUpdateFromPayload(state, payload);
        } else {
            // открытые контексты - чтобы сервер вернул новые индексы задач
            appendPayloadWithContextIds(state, payload);
        }
    },
    contextUserUpdate: (state, payload) => {
        console.log("MUTATION context/userUpdate", payload);

        if (payload?.isFromServer) {
            applyContextUpdateFromPayload(state, payload);
            const tag = payload.contextUpdate?.item;
            if (tag?.isTag) {
                syncQuickTags(state, tag);
                syncPinnedTags(state, tag);
            }
        } else {

        }
    },
    contextPos: (state, payload) => {
        console.log("MUTATION context/pos", payload);

        if (payload?.isFromServer) {

        } else {
            const contextId = payload.contextId;
            if (!contextId) return;
            const beforeContextId = payload.beforeContextId;
            if (!beforeContextId) return;
            const items = payload.items;
            if (!items?.length) return;
            const prop = payload.prop;
            if (!prop) return;

            const user = store.state.session.user;

            const first = items[0];
            const sortKey = first.auth ? "auth" : "id";
            const ids = items.map(el => el.auth || el.id)?.filter(el => !!el);
            if (!ids?.length) return;
            //console.log("BEFORE sort", list);

            // take id out
            const index = ids.findIndex(el => el === contextId);
            //console.log("index", index);
            if (index >= 0) ids.splice(index, 1);

            // place id back
            let beforeIndex = ids.findIndex(el => el === beforeContextId);
            if (beforeIndex === -1) beforeIndex = ids.length;
            //console.log("beforeIndex", beforeIndex);
            ids.splice(beforeIndex, 0, contextId);

            // todo move all sorting to BE

            // apply sort
            user[prop] = ids;
            payload[prop] = ids;
            if (typeof payload.sorter == "function") payload.sorter(items, ids, sortKey);
            else sortByAuth(items, user[prop], sortKey);
            delete payload.contextId;
            delete payload.beforeContextId;
            delete payload.items;
            delete payload.prop;
            delete payload.sort;
            //sortByAuth(items, user[prop], sortKey);
            //console.log("AFTER sort", user[prop]);
        }
    },
    contextDelete: (state, payload) => {
        console.log("MUTATION context/delete", payload);

        if (payload?.isFromServer) {
            const contextId = payload.contextDelete?.item?.id;
            if (!contextId) return;

            syncQuickTags(state, {id: contextId}, true);
            syncPinnedTags(state, {id: contextId}, true);

            const area = state.session.area;
            const zones = [
                {list: area.picks},
                {list: area.favoriteUsers},
                //{list: state.session.area.groups},
                //{list: state.session.area.topics},
                {list: area.tags},
            ];
            let context;
            for (let zone of zones) {
                if (!zone.list?.length) continue;

                //console.log("ZONE", zone.list);
                const index = zone.list.findIndex(el => el.contextId === contextId || el.id === contextId);
                if (index === -1) continue;
                context = zone.list[index];

                // delete from area
                zone.list.splice(index, 1);
                console.log("CONTEXT DELETED: " + contextId);
                break;
            }

            // go out from here if this context is deleted
            if (state.page.context.item?.contextId === contextId || state.page.context.item?.id === contextId) {
                //router.push({name: "home"});
                routeToPersonalTasks();
            }

        }

        // check we have this
        /*const contextId = payload.contextId;

        const area = state.session.area;
        const zones = [
            {list: area.picks},
            {list: area.userPicks},
            //{list: state.session.area.groups},
            //{list: state.session.area.topics},
            {list: area.tags},
        ];

        let context;
        for (let zone of zones) {
            if (!zone.list?.length) continue;

            //console.log("ZONE", zone.list);
            const index = zone.list.findIndex(el => el.contextId === contextId);
            if (index === -1) continue;
            context = zone.list[index];

            // delete from area
            zone.list.splice(index, 1);
            //console.log("DELETED " + index + ": " + contextId);
            break;
        }

        initPicksInArea(area);
        initUserPicksInArea(area);

        if (!context) return;

        // clear context
        if (state.page.context.item?.contextId === context.contextId) {
            state.page.context.item = null;
            state.page.tasks.items = null;
        }

        // broadcast to subscribers of this
        const broadcast = [{
            entity: "context",
            auth: context.contextId,
        }];
        payload.broadcast = broadcast;
        console.log("broadcast", broadcast);

        if (router.currentRoute.params?.contextId === context.contextId) {
            router.push({name: "home"});
        }*/
    },
    contextArchive: (state, payload) => {
        console.log("MUTATION context/archive", payload);
        if (!payload?.contextId) return;

        const params = {};
        if (payload.isArchived) {
            params.archivedAt = date2Iso(new Date());
            //params._isArchived = true;
            //params.isFav = false;
        } else {
            params.archivedAt = null;
            //params._isArchived = false;
        }

        mutations.contextUpdate(state, {
            contextId: payload.contextId,
            params
        });
    },

    contextSortTasksByPrio: (state, payload) => {
        console.log("MUTATION context/sortTasksByPrio", payload);
        if (!payload.contextUpdate?.item) return;

        const update = payload.contextUpdate?.item;
        const area = state.session.area;
        const contexts = jointContexts(area);
        if (!contexts?.length) return;
        const context = contexts.find(el => el.contextId === update.contextId);
        console.log("CONTEXT FOUND", update.contextId);
        console.log("CONTEXT FOUND", context);
        if (!context) return;
        context.taskSort = update.taskSort;

        if (state.page.context.item?.contextId) {
            const {rootContextId, contextId} = parseContextIds(state.page.context.item?.contextId);
            if (rootContextId === update.contextId) {
                console.log("CONTEXT TO SORT", state.page.context.item);
                state.page.context.item.taskSort = update.taskSort;
                sortByPrio(state.page.tasks.items);
            }
        }
    },

    contextClearSelectedTasks: (state, payload) => {
        //console.log("contextClearSelectedTasks()");
        //state.page.context.selectedTasks.splice(0, state.page.context.selectedTasks.length);
        Vue.set(state.page.context, "selectedTasks", []);
    },
    contextSetSelectedTasks: (state, payload) => {
        //console.log("contextSetSelectedTasks()");
        Vue.set(state.page.context, "selectedTasks", payload);
    },
    contextAppendSelectedTask: (state, payload) => {
        //console.log("contextAppendSelectedTask()", payload);
        if (!state.page.context.selectedTasks) Vue.set(state.page.context, "selectedTasks", []);
        const selectedTasks = state.page.context.selectedTasks;
        const item = payload.item;
        if (payload.withFullReplace) {
            Vue.set(state.page.context, "selectedTasks", [item]);
        } else {
            const index = selectedTasks.findIndex(el => el.id === item.id);
            if (index >= 0) {
                //if (!keepPrevious && this.selectedTasks.length > 1) this.selectedTasks.splice(index, 1);
                if (payload.deleteIfExists) selectedTasks.splice(index, 1);
            } else selectedTasks.push(item);
        }
    },

    contextTaskSort: (state, payload) => {
        console.log("MUTATION context/taskSort", payload);

        const contextAuth = payload.contextAuth;
        const topicAuth = payload.topicAuth;
        if (!contextAuth && !topicAuth) return;

        const newTaskSort = payload.taskSort;
        if (!newTaskSort?.length) return;

        // check the requested context is ours
        let context;
        if (topicAuth && topicAuth === state.page.context.topic?.auth) context = state.page.context.topic;
        else if (!topicAuth && contextAuth && contextAuth === state.page.context.context?.auth) context = state.page.context.context;
        else return;

        //console.log("CONTEXT topic", state.page.context.topic);
        //console.log("CONTEXT context", state.page.context.context);

        // apply to tasks
        const action = () => {
            const tasks = state.page.tasks.items;
            sortByAuth(tasks, newTaskSort);
        }

        // find which task is moved
        /*const sortedTab = state.page.context.tab;
        let currentTaskSort = context.taskSort;
        if ( typeof currentTaskSort === "string" ) {
            try { currentTaskSort = JSON.parse(currentTaskSort);}
            catch (e) {return;}
        }
        currentTaskSort = Array.isArray(currentTaskSort) ? currentTaskSort : currentTaskSort[sortedTab];
        const movedItem = newTaskSort.find((el, i)=>el !== currentTaskSort[i]);
        console.log("Moved item", movedItem);*/
        //uiMutations.uiSetActorOfcontextTaskSort(state, {userAuth: payload.userAuth});
        if (payload.initiatorAuth && payload.taskAuth) {
            uiMutations.uiSetActorOfTask(state, {
                userAuth: payload.userAuth,
                subjectAuth: payload.taskAuth,
                icon: "mdi-hand-back-right-outline",
                timeout: 900,
            });
            setTimeout(action, 300);
        } else action();

        // todo apply to context.taskSort
        //Vue.set(context, "taskSort", context.taskSort);
        //console.log("context.taskSort", context.taskSort);

        /*const sourceIndex = payload.sourceIndex;
        const destinationIndex = payload.destinationIndex;
        const items = state.page.tasks.items;

        if (!items) return;
        if (sourceIndex < 0 || sourceIndex >= items.length) return;
        if (destinationIndex < 0 || destinationIndex >= items.length) return;

        if (sourceIndex !== destinationIndex) {
            const source = items[sourceIndex];
            items.splice(sourceIndex, 1);
            items.splice(destinationIndex, 0, source);
        }*/

        // broadcast to subscribers of this
        const broadcast = [{
            entity: "context",
            auth: contextAuth,
        }];
        if (topicAuth) broadcast.push({
            entity: "topic",
            auth: topicAuth,
        });
        payload.broadcast = broadcast;
        console.log("broadcast", broadcast);
    },
    contextSort: (state, payload) => {
        console.log("MUTATION context/sort", payload);
        const user = state.session.user;
        if (!user) return;

        const contexts = state.session.area.contexts;
        if (!contexts) return;

        sortByAuth(contexts, payload.contextSort);
        //console.log("AFTER sort", context.children.map(c=>c.auth));

        user.contextSort = payload.contextSort;
    },
    contextAssign: (state, payload) => {
        console.log("MUTATION context/assign", payload);

        if (payload?.isFromServer) {
            applyContextUpdateFromPayload(state, payload);
        } else {
            if (!payload?.contextId) return;

            //const originalContextId = payload.contextId;
            //console.log("originalContextId: ", originalContextId);
            //const {rootContextId, contextId} = parseContextIds(originalContextId);
            const contextId = payload.contextId;
            const assigneeAuth = payload.assigneeAuth || payload.userAuth;    // can be null!
            const ownerAuth = payload.ownerAuth;    // can be null!

            // validate we can assign to this user (make sure we know him)
            let userId = null, ownerId = null;
            if (assigneeAuth) {
                const user = state.session.area.users.find(el => el.id === assigneeAuth);
                //console.log("USER FOUND", user);
                if (user) userId = user.id;
                else return;
            }

            store.commit("contextUpdate", {
                contextId,
                params: {
                    userId
                }
            });
        }
        // broadcast to subscribers of this
        /*const broadcast = [{
            entity: "context",
            auth: payload?.contextAuth,
        }];
        payload.broadcast = broadcast;
        console.log("broadcast", broadcast);*/
    },
};

export default mutations;
